/**
 * Service pour gérer la communication iframe
 * Permet l'intégration du formulaire sur d'autres sites
 */
export class IframeService {
  private static resizeObserver: ResizeObserver | null = null;
  private static lastHeight = 0;
  private static isEmbed = false;

  /**
   * Initialise la communication iframe
   */
  static init(): void {
    // Détecter si on est en mode embed
    const urlParams = new URLSearchParams(window.location.search);
    this.isEmbed = urlParams.get('embed') === 'true';

    if (this.isEmbed) {
      console.log('🖼️ Mode embed activé');
      document.body.classList.add('embed-mode');
      this.setupAutoResize();
      this.notifyReady();
    }

    // Écouter les messages du parent
    window.addEventListener('message', this.handleParentMessage);
  }

  /**
   * Configure l'auto-redimensionnement
   */
  private static setupAutoResize(): void {
    // Observer les changements de taille
    this.resizeObserver = new ResizeObserver(() => {
      this.sendHeight();
    });

    // Observer le body et le document
    this.resizeObserver.observe(document.body);
    this.resizeObserver.observe(document.documentElement);

    // Envoyer la hauteur initiale
    setTimeout(() => this.sendHeight(), 100);

    // Envoyer la hauteur à chaque changement de contenu
    const observer = new MutationObserver(() => {
      this.sendHeight();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  /**
   * Envoie la hauteur actuelle au parent
   */
  static sendHeight(): void {
    if (!this.isEmbed) return;

    const height = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );

    // Éviter d'envoyer la même hauteur plusieurs fois
    if (height !== this.lastHeight && height > 0) {
      this.lastHeight = height;
      this.postMessage({
        type: 'pemlab-resize',
        height: height + 20, // Ajouter un peu de marge
      });
    }
  }

  /**
   * Envoie un événement au parent
   */
  static sendEvent(eventName: string, data?: any): void {
    if (!this.isEmbed) return;

    this.postMessage({
      type: 'pemlab-event',
      event: eventName,
      data: data,
    });
  }

  /**
   * Notifie que le formulaire est prêt
   */
  private static notifyReady(): void {
    this.sendEvent('form-ready', {
      version: '1.0.0',
      features: ['auto-resize', 'events', 'navigation'],
    });
  }

  /**
   * Envoie la progression
   */
  static sendProgress(current: number, total: number, points?: number): void {
    this.sendEvent('step-change', {
      currentStep: current,
      totalSteps: total,
      progress: Math.round((current / total) * 100),
      points: points || 0,
    });
  }

  /**
   * Envoie la completion
   */
  static sendComplete(products: string[], totalPoints: number): void {
    const redirectUrl = `https://pemlab-paris.fr/collections/all/${products.join('+')}`;

    this.sendEvent('form-complete', {
      products,
      totalPoints,
      redirectUrl,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Envoie une erreur
   */
  static sendError(error: string): void {
    this.sendEvent('form-error', {
      message: error,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Gère les messages du parent
   */
  private static handleParentMessage = (event: MessageEvent): void => {
    // Vérifier l'origine pour la sécurité
    if (!this.isAllowedOrigin(event.origin)) return;

    if (event.data?.type === 'pemlab-command') {
      console.log('📨 Command reçue:', event.data);
      this.handleCommand(event.data.command, event.data.data);
    }
  };

  /**
   * Traite les commandes du parent
   */
  private static handleCommand(command: string, data?: any): void {
    switch (command) {
      case 'reset':
        // Réinitialiser le formulaire
        window.location.reload();
        break;

      case 'set-theme':
        // Changer le thème
        if (data?.theme) {
          document.documentElement.setAttribute('data-theme', data.theme);
        }
        break;

      case 'get-state':
        // Envoyer l'état actuel
        this.sendEvent('state', {
          currentStep: this.getCurrentStep(),
          totalSteps: this.getTotalSteps(),
          isComplete: false,
        });
        break;

      case 'go-to-step':
        // Naviguer vers une étape spécifique
        if (typeof data?.step === 'number') {
          // Émettre un événement personnalisé pour que l'app réagisse
          window.dispatchEvent(
            new CustomEvent('pemlab-navigate', {
              detail: { step: data.step },
            })
          );
        }
        break;
    }
  }

  /**
   * Vérifie si l'origine est autorisée
   */
  private static isAllowedOrigin(origin: string): boolean {
    const allowedOrigins = [
      'https://pemlab-paris.fr',
      'https://www.pemlab-paris.fr',
      // Ajouter d'autres domaines autorisés ici
    ];

    // En développement, autoriser localhost
    if (import.meta.env.DEV) {
      allowedOrigins.push('http://localhost:3000');
      allowedOrigins.push('http://localhost:5173');
      allowedOrigins.push('http://127.0.0.1:5173');
    }

    return allowedOrigins.includes(origin) || origin === window.location.origin;
  }

  /**
   * Envoie un message au parent
   */
  private static postMessage(data: any): void {
    if (window.parent !== window) {
      window.parent.postMessage(data, '*');
    }
  }

  /**
   * Helpers pour récupérer l'état actuel
   */
  private static getCurrentStep(): number {
    // À implémenter selon votre logique
    return parseInt(sessionStorage.getItem('pemlab-current-step') || '0');
  }

  private static getTotalSteps(): number {
    // À implémenter selon votre logique
    return parseInt(sessionStorage.getItem('pemlab-total-steps') || '10');
  }

  /**
   * Stocke l'état pour les helpers
   */
  static updateState(currentStep: number, totalSteps: number): void {
    sessionStorage.setItem('pemlab-current-step', currentStep.toString());
    sessionStorage.setItem('pemlab-total-steps', totalSteps.toString());
  }

  /**
   * Nettoie les observateurs
   */
  static destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    window.removeEventListener('message', this.handleParentMessage);
  }

  /**
   * Vérifie si on est en mode embed
   */
  static isEmbedMode(): boolean {
    return this.isEmbed;
  }
}
